import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { SEO } from '@/components/SEO'
import { ProductHeroSection } from './ProductHeroSection'
import { ProductDetailsTabs } from './ProductDetailsTabs'
import { RelatedRecipesSection } from './RelatedRecipesSection'
import type { SupportedLanguage } from '@/i18n'

// Product data - in a real app this would come from an API
const productsData: Record<string, Record<SupportedLanguage, {
  name: string
  tagline: string
  description: string
  features: string[]
  certifications: string[]
  ingredients: string[]
  nutrition: {
    servingSize: string
    energy: string
    fat: string
    saturatedFat: string
    carbs: string
    sugars: string
    protein: string
    salt: string
  }
  usageSuggestions: string[]
  formats: { name: string; weight: string }[]
  image: string
  relatedRecipes: string[]
}>> = {
  'mortadella-suprema': {
    it: {
      name: 'Mortadella Suprema',
      tagline: 'La Regina delle Mortadelle',
      description: 'La Mortadella Suprema Fiorucci rappresenta l\'eccellenza della tradizione norcina. Prodotta con carni selezionate di prima qualità, si distingue per la sua consistenza morbida, l\'aroma delicato e il sapore inconfondibile che la rendono unica.',
      features: [
        'Senza glutine',
        'Senza derivati del latte',
        'Carni selezionate di prima qualità',
        'Tradizione norcina dal 1850'
      ],
      certifications: ['senza-glutine'],
      ingredients: [
        'Carne di suino',
        'Lardelli di suino',
        'Sale',
        'Aromi naturali',
        'Spezie',
        'Destrosio',
        'Saccarosio',
        'Antiossidante: ascorbato di sodio',
        'Conservante: nitrito di sodio'
      ],
      nutrition: {
        servingSize: '50g',
        energy: '142 kcal',
        fat: '13g',
        saturatedFat: '4.5g',
        carbs: '0.5g',
        sugars: '0g',
        protein: '6g',
        salt: '1.2g'
      },
      usageSuggestions: [
        'Ideale per antipasti e aperitivi',
        'Perfetta per panini gourmet',
        'Ottima in insalate e piatti freddi',
        'Da abbinare a formaggi stagionati',
        'Perfetta sulla pizza bianca'
      ],
      formats: [
        { name: 'Intera', weight: '10-12 kg' },
        { name: 'Mezza', weight: '5-6 kg' },
        { name: 'Quarto', weight: '2.5-3 kg' },
        { name: 'Fette', weight: '100-200g' },
        { name: 'Affettato', weight: '80-150g' }
      ],
      image: '/images/Suprema amb-sito - thumb.jpg',
      relatedRecipes: ['pizza-bianca', 'lasagne', 'crostini-mortadella']
    },
    en: {
      name: 'Suprema Mortadella',
      tagline: 'The Queen of Mortadellas',
      description: 'Fiorucci Suprema Mortadella represents the excellence of Norcinian tradition. Made with selected premium quality meats, it stands out for its soft texture, delicate aroma and unmistakable flavor that make it unique.',
      features: [
        'Gluten free',
        'Without milk derivatives',
        'Selected premium quality meats',
        'Norcinian tradition since 1850'
      ],
      certifications: ['senza-glutine'],
      ingredients: [
        'Pork meat',
        'Pork fat cubes',
        'Salt',
        'Natural flavors',
        'Spices',
        'Dextrose',
        'Sucrose',
        'Antioxidant: sodium ascorbate',
        'Preservative: sodium nitrite'
      ],
      nutrition: {
        servingSize: '50g',
        energy: '142 kcal',
        fat: '13g',
        saturatedFat: '4.5g',
        carbs: '0.5g',
        sugars: '0g',
        protein: '6g',
        salt: '1.2g'
      },
      usageSuggestions: [
        'Ideal for appetizers and aperitifs',
        'Perfect for gourmet sandwiches',
        'Excellent in salads and cold dishes',
        'Pair with aged cheeses',
        'Perfect on white pizza'
      ],
      formats: [
        { name: 'Whole', weight: '10-12 kg' },
        { name: 'Half', weight: '5-6 kg' },
        { name: 'Quarter', weight: '2.5-3 kg' },
        { name: 'Slices', weight: '100-200g' },
        { name: 'Pre-sliced', weight: '80-150g' }
      ],
      image: '/images/Suprema amb-sito - thumb.jpg',
      relatedRecipes: ['pizza-bianca', 'lasagne', 'crostini-mortadella']
    }
  },
  'prosciutto-parma': {
    it: {
      name: 'Prosciutto di Parma',
      tagline: 'Il Re dei Prosciutti',
      description: 'Il Prosciutto di Parma Fiorucci è un capolavoro della tradizione italiana. Stagionato con cura e pazienza secondo i metodi antichi, offre un sapore dolce e delicato che lo rende perfetto per ogni occasione.',
      features: [
        'DOP - Denominazione di Origine Protetta',
        'Stagionatura minima 12 mesi',
        'Solo sale marino',
        'Senza conservanti aggiunti'
      ],
      certifications: ['dop'],
      ingredients: [
        'Coscia di suino italiano',
        'Sale marino'
      ],
      nutrition: {
        servingSize: '30g',
        energy: '75 kcal',
        fat: '4g',
        saturatedFat: '1.5g',
        carbs: '0g',
        sugars: '0g',
        protein: '9g',
        salt: '1.1g'
      },
      usageSuggestions: [
        'Servito a fette sottili come antipasto',
        'Abbinato a melone fresco',
        'Con mozzarella di bufala',
        'Su bruschette calde',
        'In piatti di pasta fresca'
      ],
      formats: [
        { name: 'Intero con osso', weight: '9-11 kg' },
        { name: 'Disossato', weight: '7-8 kg' },
        { name: 'Fette', weight: '100-200g' },
        { name: 'Affettato', weight: '80-150g' }
      ],
      image: '/images/crudi.png',
      relatedRecipes: ['crostini', 'pasta-prosciutto', 'insalata-prosciutto']
    },
    en: {
      name: 'Parma Ham',
      tagline: 'The King of Hams',
      description: 'Fiorucci Parma Ham is a masterpiece of Italian tradition. Aged with care and patience according to ancient methods, it offers a sweet and delicate flavor that makes it perfect for every occasion.',
      features: [
        'PDO - Protected Designation of Origin',
        'Minimum 12 months aging',
        'Only sea salt',
        'No added preservatives'
      ],
      certifications: ['dop'],
      ingredients: [
        'Italian pork leg',
        'Sea salt'
      ],
      nutrition: {
        servingSize: '30g',
        energy: '75 kcal',
        fat: '4g',
        saturatedFat: '1.5g',
        carbs: '0g',
        sugars: '0g',
        protein: '9g',
        salt: '1.1g'
      },
      usageSuggestions: [
        'Served in thin slices as an appetizer',
        'Paired with fresh melon',
        'With buffalo mozzarella',
        'On warm bruschetta',
        'In fresh pasta dishes'
      ],
      formats: [
        { name: 'Whole with bone', weight: '9-11 kg' },
        { name: 'Boneless', weight: '7-8 kg' },
        { name: 'Slices', weight: '100-200g' },
        { name: 'Pre-sliced', weight: '80-150g' }
      ],
      image: '/images/crudi.png',
      relatedRecipes: ['crostini', 'pasta-prosciutto', 'insalata-prosciutto']
    }
  },
  'mortadella': {
    it: {
      name: 'Mortadella Suprema',
      tagline: 'La Regina delle Mortadelle',
      description: 'La Mortadella Suprema Fiorucci rappresenta l\'eccellenza della tradizione norcina. Prodotta con carni selezionate di prima qualità, si distingue per la sua consistenza morbida, l\'aroma delicato e il sapore inconfondibile che la rendono unica.',
      features: [
        'Senza glutine',
        'Senza derivati del latte',
        'Carni selezionate di prima qualità',
        'Tradizione norcina dal 1850'
      ],
      certifications: ['senza-glutine'],
      ingredients: [
        'Carne di suino',
        'Lardelli di suino',
        'Sale',
        'Aromi naturali',
        'Spezie',
        'Destrosio',
        'Saccarosio',
        'Antiossidante: ascorbato di sodio',
        'Conservante: nitrito di sodio'
      ],
      nutrition: {
        servingSize: '50g',
        energy: '142 kcal',
        fat: '13g',
        saturatedFat: '4.5g',
        carbs: '0.5g',
        sugars: '0g',
        protein: '6g',
        salt: '1.2g'
      },
      usageSuggestions: [
        'Ideale per antipasti e aperitivi',
        'Perfetta per panini gourmet',
        'Ottima in insalate e piatti freddi',
        'Da abbinare a formaggi stagionati',
        'Perfetta sulla pizza bianca'
      ],
      formats: [
        { name: 'Intera', weight: '10-12 kg' },
        { name: 'Mezza', weight: '5-6 kg' },
        { name: 'Quarto', weight: '2.5-3 kg' },
        { name: 'Fette', weight: '100-200g' },
        { name: 'Affettato', weight: '80-150g' }
      ],
      image: '/images/Mortadella_Suprema-1850_FIORUCCI_421X421_THUMB.jpg',
      relatedRecipes: ['pizza-bianca', 'lasagne', 'crostini-mortadella']
    },
    en: {
      name: 'Suprema Mortadella',
      tagline: 'The Queen of Mortadellas',
      description: 'Fiorucci Suprema Mortadella represents the excellence of Norcinian tradition. Made with selected premium quality meats, it stands out for its soft texture, delicate aroma and unmistakable flavor that make it unique.',
      features: [
        'Gluten free',
        'Without milk derivatives',
        'Selected premium quality meats',
        'Norcinian tradition since 1850'
      ],
      certifications: ['senza-glutine'],
      ingredients: [
        'Pork meat',
        'Pork fat cubes',
        'Salt',
        'Natural flavors',
        'Spices',
        'Dextrose',
        'Sucrose',
        'Antioxidant: sodium ascorbate',
        'Preservative: sodium nitrite'
      ],
      nutrition: {
        servingSize: '50g',
        energy: '142 kcal',
        fat: '13g',
        saturatedFat: '4.5g',
        carbs: '0.5g',
        sugars: '0g',
        protein: '6g',
        salt: '1.2g'
      },
      usageSuggestions: [
        'Ideal for appetizers and aperitifs',
        'Perfect for gourmet sandwiches',
        'Excellent in salads and cold dishes',
        'Pair with aged cheeses',
        'Perfect on white pizza'
      ],
      formats: [
        { name: 'Whole', weight: '10-12 kg' },
        { name: 'Half', weight: '5-6 kg' },
        { name: 'Quarter', weight: '2.5-3 kg' },
        { name: 'Slices', weight: '100-200g' },
        { name: 'Pre-sliced', weight: '80-150g' }
      ],
      image: '/images/Mortadella_Suprema-1850_FIORUCCI_421X421_THUMB.jpg',
      relatedRecipes: ['pizza-bianca', 'lasagne', 'crostini-mortadella']
    }
  },
  'prosciutto': {
    it: {
      name: 'Prosciutto Cotto Fiordicotto',
      tagline: 'La Delicatezza del Gusto',
      description: 'Il Prosciutto Cotto Fiordicotto Fiorucci è sinonimo di delicatezza e qualità. Ottenuto da cosce selezionate di suino italiano, è caratterizzato da una consistenza morbida, un colore rosa delicato e un sapore dolce che lo rende perfetto per tutta la famiglia.',
      features: [
        'Senza glutine',
        'Senza lattosio',
        '100% carne italiana',
        'Cotto a bassa temperatura'
      ],
      certifications: ['senza-glutine'],
      ingredients: [
        'Coscia di suino italiano',
        'Sale',
        'Aromi naturali',
        'Conservante: nitrito di sodio'
      ],
      nutrition: {
        servingSize: '50g',
        energy: '110 kcal',
        fat: '4g',
        saturatedFat: '1.5g',
        carbs: '1g',
        sugars: '0g',
        protein: '18g',
        salt: '1.3g'
      },
      usageSuggestions: [
        'Perfetto per panini',
        'Ideale per piatti freddi',
        'Ottimo in insalate',
        'Eccellente per involtini'
      ],
      formats: [
        { name: 'Intero', weight: '7-8 kg' },
        { name: 'Mezzo', weight: '3.5-4 kg' },
        { name: 'Fette', weight: '100-200g' },
        { name: 'Affettato', weight: '80-150g' }
      ],
      image: '/images/Cotto_Fiordicotto_FIORUCCI_421X421_THUMB.jpg',
      relatedRecipes: ['pasta-prosciutto', 'insalata-prosciutto', 'crostini-prosciutto']
    },
    en: {
      name: 'Fiordicotto Cooked Ham',
      tagline: 'The Delicacy of Taste',
      description: 'Fiordicotto Cooked Ham is synonymous with delicacy and quality. Obtained from selected Italian pork legs, it features a soft texture, delicate pink color and sweet flavor that makes it perfect for the whole family.',
      features: [
        'Gluten free',
        'Lactose free',
        '100% Italian meat',
        'Low temperature cooked'
      ],
      certifications: ['senza-glutine'],
      ingredients: [
        'Italian pork leg',
        'Salt',
        'Natural flavors',
        'Preservative: sodium nitrite'
      ],
      nutrition: {
        servingSize: '50g',
        energy: '110 kcal',
        fat: '4g',
        saturatedFat: '1.5g',
        carbs: '1g',
        sugars: '0g',
        protein: '18g',
        salt: '1.3g'
      },
      usageSuggestions: [
        'Perfect for sandwiches',
        'Ideal for cold dishes',
        'Excellent in salads',
        'Great for rolls'
      ],
      formats: [
        { name: 'Whole', weight: '7-8 kg' },
        { name: 'Half', weight: '3.5-4 kg' },
        { name: 'Slices', weight: '100-200g' },
        { name: 'Pre-sliced', weight: '80-150g' }
      ],
      image: '/images/Cotto_Fiordicotto_FIORUCCI_421X421_THUMB.jpg',
      relatedRecipes: ['pasta-prosciutto', 'insalata-prosciutto', 'crostini-prosciutto']
    }
  },
  'guanciale': {
    it: {
      name: 'Guanciale Stagionato',
      tagline: 'L\'Anima della Cucina Romana',
      description: 'Il Guanciale Stagionato Fiorucci è un prodotto della tradizione italiana, insostituibile nella preparazione della carbonara e dell\'amatriciana. Stagionato con cura, sviluppa un sapore intenso e una consistenza che si scioglie in padella.',
      features: [
        'Senza glutine',
        'Stagionatura naturale',
        'Taglio tradizionale',
        'Sapore intenso'
      ],
      certifications: [],
      ingredients: [
        'Guancia di suino',
        'Sale',
        'Pepe nero',
        'Aromi naturali',
        'Conservanti'
      ],
      nutrition: {
        servingSize: '30g',
        energy: '130 kcal',
        fat: '12g',
        saturatedFat: '4.5g',
        carbs: '0g',
        sugars: '0g',
        protein: '6g',
        salt: '1.4g'
      },
      usageSuggestions: [
        'Ideale per carbonara e amatriciana',
        'Perfetto per crostini',
        'Ottimo nella pasta',
        'Da gustare anche a fette'
      ],
      formats: [
        { name: 'Intero', weight: '1.5-2 kg' },
        { name: 'Trancio', weight: '500g' },
        { name: 'Cubetti', weight: '200g' },
        { name: 'Fette', weight: '100g' }
      ],
      image: '/images/Anteprima-Homepage-Guanciale.jpg',
      relatedRecipes: ['carbonara', 'amatriciana', 'crostini-guanciale']
    },
    en: {
      name: 'Aged Guanciale',
      tagline: 'The Soul of Roman Cuisine',
      description: 'Fiorucci Aged Guanciale is a product of Italian tradition, irreplaceable in the preparation of carbonara and amatriciana. Carefully aged, it develops an intense flavor and a consistency that melts in the pan.',
      features: [
        'Gluten free',
        'Natural aging',
        'Traditional cut',
        'Intense flavor'
      ],
      certifications: [],
      ingredients: [
        'Pork jowl',
        'Salt',
        'Black pepper',
        'Natural flavors',
        'Preservatives'
      ],
      nutrition: {
        servingSize: '30g',
        energy: '130 kcal',
        fat: '12g',
        saturatedFat: '4.5g',
        carbs: '0g',
        sugars: '0g',
        protein: '6g',
        salt: '1.4g'
      },
      usageSuggestions: [
        'Ideal for carbonara and amatriciana',
        'Perfect for crostini',
        'Excellent in pasta',
        'Also delicious sliced'
      ],
      formats: [
        { name: 'Whole', weight: '1.5-2 kg' },
        { name: 'Chunk', weight: '500g' },
        { name: 'Cubes', weight: '200g' },
        { name: 'Slices', weight: '100g' }
      ],
      image: '/images/Anteprima-Homepage-Guanciale.jpg',
      relatedRecipes: ['carbonara', 'amatriciana', 'crostini-guanciale']
    }
  },
  'affettati': {
    it: {
      name: 'Linea Affettati Fiorucci',
      tagline: 'Qualità in Ogni Fetta',
      description: 'La linea degli affettati Fiorucci raccoglie il meglio della tradizione salumiera in pratiche confezioni pronte all\'uso. Dalla mortadella al prosciutto cotto, ogni fetta è un concentrato di qualità e sapore autentico.',
      features: [
        'Pratico e versatile',
        'Confezioni sigillate',
        'Senza glutine',
        'Freschezza garantita'
      ],
      certifications: ['senza-glutine'],
      ingredients: [
        'Varia in base al prodotto (carne di suino, sale, aromi naturali, conservanti)'
      ],
      nutrition: {
        servingSize: '30g',
        energy: '105 kcal',
        fat: '8g',
        saturatedFat: '3g',
        carbs: '0.5g',
        sugars: '0g',
        protein: '8g',
        salt: '1.2g'
      },
      usageSuggestions: [
        'Perfetto per aperitivi',
        'Ideale per panini veloci',
        'Ottimo su tavole di salumi',
        'Da gustare in insalata'
      ],
      formats: [
        { name: 'Vaschetta', weight: '80g' },
        { name: 'Vaschetta', weight: '100g' },
        { name: 'Vaschetta', weight: '120g' },
        { name: 'Maxi-pack', weight: '200g' }
      ],
      image: '/images/affettati.png',
      relatedRecipes: ['crostini', 'tagliere-affettati', 'panini-gourmet']
    },
    en: {
      name: 'Fiorucci Sliced Meats Line',
      tagline: 'Quality in Every Slice',
      description: 'The Fiorucci sliced meats line brings the best of cured meat tradition in practical ready-to-use packs. From mortadella to cooked ham, every slice is a concentrate of quality and authentic flavor.',
      features: [
        'Practical and versatile',
        'Sealed packs',
        'Gluten free',
        'Guaranteed freshness'
      ],
      certifications: ['senza-glutine'],
      ingredients: [
        'Varies by product (pork meat, salt, natural flavors, preservatives)'
      ],
      nutrition: {
        servingSize: '30g',
        energy: '105 kcal',
        fat: '8g',
        saturatedFat: '3g',
        carbs: '0.5g',
        sugars: '0g',
        protein: '8g',
        salt: '1.2g'
      },
      usageSuggestions: [
        'Perfect for aperitifs',
        'Ideal for quick sandwiches',
        'Excellent on charcuterie boards',
        'Great in salads'
      ],
      formats: [
        { name: 'Tray', weight: '80g' },
        { name: 'Tray', weight: '100g' },
        { name: 'Tray', weight: '120g' },
        { name: 'Maxi-pack', weight: '200g' }
      ],
      image: '/images/affettati.png',
      relatedRecipes: ['crostini', 'tagliere-affettati', 'panini-gourmet']
    }
  },
  'salami': {
    it: {
      name: 'Salami Fiorucci',
      tagline: 'Il Gusto Autentico',
      description: 'I Salami Fiorucci rappresentano l\'autentica tradizione salumiera italiana. Stagionati con cura e passione secondo ricette antiche, sviluppano un sapore deciso e un aroma inconfondibile che li rendono perfetti per ogni occasione.',
      features: [
        'Senza glutine',
        'Stagionatura naturale',
        'Carni selezionate di prima qualità',
        'Tradizione salumiera dal 1850'
      ],
      certifications: ['senza-glutine'],
      ingredients: [
        'Carne di suino',
        'Grasso di suino',
        'Sale',
        'Aromi naturali',
        'Spezie',
        'Vino',
        'Conservante: nitrito di sodio'
      ],
      nutrition: {
        servingSize: '30g',
        energy: '115 kcal',
        fat: '9g',
        saturatedFat: '3.5g',
        carbs: '0.5g',
        sugars: '0g',
        protein: '8g',
        salt: '1.3g'
      },
      usageSuggestions: [
        'Ideali per antipasti e taglieri',
        'Perfetti su pane e crostini',
        'Ottimi in insalate',
        'Da abbinare a formaggi stagionati'
      ],
      formats: [
        { name: 'Intero', weight: '400-500g' },
        { name: 'Metà', weight: '200-250g' },
        { name: 'Fette', weight: '80-150g' },
        { name: 'Affettato', weight: '80g' }
      ],
      image: '/images/salami.png',
      relatedRecipes: ['tagliere-affettati', 'crostini-misti']
    },
    en: {
      name: 'Fiorucci Salami',
      tagline: 'Authentic Taste',
      description: 'Fiorucci Salami represents the authentic Italian cured meat tradition. Carefully aged with passion according to ancient recipes, they develop a bold flavor and unmistakable aroma that make them perfect for any occasion.',
      features: [
        'Gluten free',
        'Natural aging',
        'Selected premium quality meats',
        'Salumiera tradition since 1850'
      ],
      certifications: ['senza-glutine'],
      ingredients: [
        'Pork meat',
        'Pork fat',
        'Salt',
        'Natural flavors',
        'Spices',
        'Wine',
        'Preservative: sodium nitrite'
      ],
      nutrition: {
        servingSize: '30g',
        energy: '115 kcal',
        fat: '9g',
        saturatedFat: '3.5g',
        carbs: '0.5g',
        sugars: '0g',
        protein: '8g',
        salt: '1.3g'
      },
      usageSuggestions: [
        'Ideal for appetizers and charcuterie boards',
        'Perfect on bread and crostini',
        'Excellent in salads',
        'Pair with aged cheeses'
      ],
      formats: [
        { name: 'Whole', weight: '400-500g' },
        { name: 'Half', weight: '200-250g' },
        { name: 'Slices', weight: '80-150g' },
        { name: 'Pre-sliced', weight: '80g' }
      ],
      image: '/images/salami.png',
      relatedRecipes: ['tagliere-affettati', 'crostini-misti']
    }
  },
  'arrosti': {
    it: {
      name: 'Arrosti Fiorucci',
      tagline: 'La Ricchezza del Sapore',
      description: 'Gli Arrosti Fiorucci sono preparati con carni selezionate e cotti a bassa temperatura per garantire tenerezza e succulenza. Ogni fetta racconta la tradizione norcina e l\'impegno quotidiano per offrire solo il meglio.',
      features: [
        '100% carne italiana',
        'Cotto a bassa temperatura',
        'Senza glutine',
        'Tenerezza garantita'
      ],
      certifications: ['senza-glutine'],
      ingredients: [
        'Carne di suino italiano',
        'Sale',
        'Aromi naturali',
        'Spezie',
        'Antiossidante: ascorbato di sodio',
        'Conservante: nitrito di sodio'
      ],
      nutrition: {
        servingSize: '50g',
        energy: '120 kcal',
        fat: '7g',
        saturatedFat: '2.5g',
        carbs: '0.5g',
        sugars: '0g',
        protein: '14g',
        salt: '1.1g'
      },
      usageSuggestions: [
        'Perfetti per piatti freddi',
        'Ideali per panini e tramezzini',
        'Ottimi in insalate',
        'Da gustare con salse e mostarde'
      ],
      formats: [
        { name: 'Intero', weight: '2-3 kg' },
        { name: 'Trancio', weight: '500g' },
        { name: 'Fette', weight: '100-200g' },
        { name: 'Affettato', weight: '80-150g' }
      ],
      image: '/images/arrosti.jpg',
      relatedRecipes: ['panini-gourmet', 'insalata-prosciutto']
    },
    en: {
      name: 'Fiorucci Roasts',
      tagline: 'The Richness of Flavor',
      description: 'Fiorucci Roasts are prepared with selected meats and slow-cooked at low temperature to guarantee tenderness and juiciness. Every slice tells the Norcinian tradition and the daily commitment to offer only the best.',
      features: [
        '100% Italian meat',
        'Low temperature cooked',
        'Gluten free',
        'Guaranteed tenderness'
      ],
      certifications: ['senza-glutine'],
      ingredients: [
        'Italian pork meat',
        'Salt',
        'Natural flavors',
        'Spices',
        'Antioxidant: sodium ascorbate',
        'Preservative: sodium nitrite'
      ],
      nutrition: {
        servingSize: '50g',
        energy: '120 kcal',
        fat: '7g',
        saturatedFat: '2.5g',
        carbs: '0.5g',
        sugars: '0g',
        protein: '14g',
        salt: '1.1g'
      },
      usageSuggestions: [
        'Perfect for cold dishes',
        'Ideal for sandwiches and wraps',
        'Excellent in salads',
        'Enjoy with sauces and mustards'
      ],
      formats: [
        { name: 'Whole', weight: '2-3 kg' },
        { name: 'Chunk', weight: '500g' },
        { name: 'Slices', weight: '100-200g' },
        { name: 'Pre-sliced', weight: '80-150g' }
      ],
      image: '/images/arrosti.jpg',
      relatedRecipes: ['panini-gourmet', 'insalata-prosciutto']
    }
  },
  'wurstel': {
    it: {
      name: 'Wurstel Fiorucci',
      tagline: 'Tradizione e Bontà',
      description: 'I Wurstel Fiorucci uniscono il gusto delicato della migliore tradizione italiana alla praticità del quotidiano. Ideali per tutta la famiglia, sono versatili in cucina e perfetti per preparare piatti semplici e gustosi.',
      features: [
        'Senza glutine',
        'Senza coloranti',
        'Facili da preparare',
        'Ideali per tutta la famiglia'
      ],
      certifications: ['senza-glutine'],
      ingredients: [
        'Carne di suino',
        'Acqua',
        'Sale',
        'Aromi naturali',
        'Spezie',
        'Destrosio',
        'Antiossidante: ascorbato di sodio',
        'Conservante: nitrito di sodio'
      ],
      nutrition: {
        servingSize: '50g',
        energy: '135 kcal',
        fat: '11g',
        saturatedFat: '4g',
        carbs: '1g',
        sugars: '0g',
        protein: '8g',
        salt: '1.4g'
      },
      usageSuggestions: [
        'Perfetti per piatti veloci',
        'Ideali per buffet e feste',
        'Ottimi in padella e al forno',
        'Da gustare con patate e crauti'
      ],
      formats: [
        { name: 'Confezione', weight: '250g' },
        { name: 'Confezione', weight: '500g' },
        { name: 'Maxi-pack', weight: '1 kg' }
      ],
      image: '/images/wurstel.png',
      relatedRecipes: ['panini-gourmet']
    },
    en: {
      name: 'Fiorucci Wurstel',
      tagline: 'Tradition and Goodness',
      description: 'Fiorucci Wurstel combines the delicate taste of the best Italian tradition with everyday practicality. Ideal for the whole family, they are versatile in the kitchen and perfect for preparing simple and tasty dishes.',
      features: [
        'Gluten free',
        'No colorants',
        'Easy to prepare',
        'Ideal for the whole family'
      ],
      certifications: ['senza-glutine'],
      ingredients: [
        'Pork meat',
        'Water',
        'Salt',
        'Natural flavors',
        'Spices',
        'Dextrose',
        'Antioxidant: sodium ascorbate',
        'Preservative: sodium nitrite'
      ],
      nutrition: {
        servingSize: '50g',
        energy: '135 kcal',
        fat: '11g',
        saturatedFat: '4g',
        carbs: '1g',
        sugars: '0g',
        protein: '8g',
        salt: '1.4g'
      },
      usageSuggestions: [
        'Perfect for quick meals',
        'Ideal for buffets and parties',
        'Excellent pan-fried and baked',
        'Enjoy with potatoes and sauerkraut'
      ],
      formats: [
        { name: 'Pack', weight: '250g' },
        { name: 'Pack', weight: '500g' },
        { name: 'Maxi-pack', weight: '1 kg' }
      ],
      image: '/images/wurstel.png',
      relatedRecipes: ['panini-gourmet']
    }
  },
  'cubetti-tranci': {
    it: {
      name: 'Cubetti e Tranci Fiorucci',
      tagline: 'Versatilità in Cucina',
      description: 'Cubetti e Tranci Fiorucci sono gli alleati perfetti in cucina. Pratici e versatili, permettono di preparare con creatività tantissime ricette, dalla carbonaria all\'amatriciana, donando sapore e consistenza a ogni piatto.',
      features: [
        'Pratici e versatili',
        'Ideali per cucinare',
        'Senza glutine',
        'Taglio uniforme'
      ],
      certifications: ['senza-glutine'],
      ingredients: [
        'Carne di suino',
        'Grasso di suino',
        'Sale',
        'Aromi naturali',
        'Spezie',
        'Conservante: nitrito di sodio'
      ],
      nutrition: {
        servingSize: '50g',
        energy: '145 kcal',
        fat: '12g',
        saturatedFat: '4.5g',
        carbs: '0.5g',
        sugars: '0g',
        protein: '9g',
        salt: '1.5g'
      },
      usageSuggestions: [
        'Ideali per carbonara e amatriciana',
        'Perfetti per soffritti',
        'Ottimi su pizza',
        'Da aggiungere a zuppe e minestre'
      ],
      formats: [
        { name: 'Cubetti', weight: '200g' },
        { name: 'Tranci', weight: '250g' },
        { name: 'Cubetti', weight: '500g' }
      ],
      image: '/images/cubetti-tranci.png',
      relatedRecipes: ['carbonara', 'amatriciana']
    },
    en: {
      name: 'Fiorucci Cubes and Chunks',
      tagline: 'Versatility in the Kitchen',
      description: 'Fiorucci Cubes and Chunks are the perfect allies in the kitchen. Practical and versatile, they allow you to creatively prepare countless recipes, from carbonara to amatriciana, adding flavor and texture to every dish.',
      features: [
        'Practical and versatile',
        'Ideal for cooking',
        'Gluten free',
        'Uniform cut'
      ],
      certifications: ['senza-glutine'],
      ingredients: [
        'Pork meat',
        'Pork fat',
        'Salt',
        'Natural flavors',
        'Spices',
        'Preservative: sodium nitrite'
      ],
      nutrition: {
        servingSize: '50g',
        energy: '145 kcal',
        fat: '12g',
        saturatedFat: '4.5g',
        carbs: '0.5g',
        sugars: '0g',
        protein: '9g',
        salt: '1.5g'
      },
      usageSuggestions: [
        'Ideal for carbonara and amatriciana',
        'Perfect for sautés',
        'Excellent on pizza',
        'Add to soups and stews'
      ],
      formats: [
        { name: 'Cubes', weight: '200g' },
        { name: 'Chunks', weight: '250g' },
        { name: 'Cubes', weight: '500g' }
      ],
      image: '/images/cubetti-tranci.png',
      relatedRecipes: ['carbonara', 'amatriciana']
    }
  },
  'precotti': {
    it: {
      name: 'Precotti Fiorucci',
      tagline: 'Praticità e Qualità',
      description: 'I Precotti Fiorucci offrono la perfetta combinazione tra praticità e qualità. Prodotti pronti da scaldare e gustare, ideali per chi cerca soluzioni veloci senza rinunciare al gusto autentico della tradizione italiana.',
      features: [
        'Pronti in pochi minuti',
        'Senza glutine',
        'Pratici e versatili',
        'Qualità Fiorucci garantita'
      ],
      certifications: ['senza-glutine'],
      ingredients: [
        'Carne di suino',
        'Sale',
        'Aromi naturali',
        'Spezie',
        'Conservante: nitrito di sodio'
      ],
      nutrition: {
        servingSize: '50g',
        energy: '130 kcal',
        fat: '9g',
        saturatedFat: '3.5g',
        carbs: '1g',
        sugars: '0g',
        protein: '11g',
        salt: '1.2g'
      },
      usageSuggestions: [
        'Pronti in padella o al microonde',
        'Ideali per piatti veloci',
        'Perfetti per cene in famiglia',
        'Ottimi con contorni freschi'
      ],
      formats: [
        { name: 'Vaschetta', weight: '200g' },
        { name: 'Vaschetta', weight: '400g' },
        { name: 'Family pack', weight: '800g' }
      ],
      image: '/images/precotti.jpg',
      relatedRecipes: ['panini-gourmet']
    },
    en: {
      name: 'Fiorucci Pre-cooked',
      tagline: 'Practicality and Quality',
      description: 'Fiorucci Pre-cooked products offer the perfect combination of convenience and quality. Ready-to-heat and enjoy, ideal for those looking for quick solutions without giving up the authentic taste of Italian tradition.',
      features: [
        'Ready in minutes',
        'Gluten free',
        'Practical and versatile',
        'Guaranteed Fiorucci quality'
      ],
      certifications: ['senza-glutine'],
      ingredients: [
        'Pork meat',
        'Salt',
        'Natural flavors',
        'Spices',
        'Preservative: sodium nitrite'
      ],
      nutrition: {
        servingSize: '50g',
        energy: '130 kcal',
        fat: '9g',
        saturatedFat: '3.5g',
        carbs: '1g',
        sugars: '0g',
        protein: '11g',
        salt: '1.2g'
      },
      usageSuggestions: [
        'Ready in the pan or microwave',
        'Ideal for quick meals',
        'Perfect for family dinners',
        'Excellent with fresh side dishes'
      ],
      formats: [
        { name: 'Tray', weight: '200g' },
        { name: 'Tray', weight: '400g' },
        { name: 'Family pack', weight: '800g' }
      ],
      image: '/images/precotti.jpg',
      relatedRecipes: ['panini-gourmet']
    }
  },
}

// Default product fallback
const defaultProduct: Record<SupportedLanguage, typeof productsData[string][SupportedLanguage]> = {
  it: {
    name: 'Prodotto Fiorucci',
    tagline: 'Qualità e Tradizione',
    description: 'Scopri l\'eccellenza dei salumi Fiorucci, prodotti con passione e tradizione dal 1850.',
    features: ['Senza glutine', 'Carni selezionate', 'Tradizione norcina'],
    certifications: ['senza-glutine'],
    ingredients: ['Carne di suino selezionata', 'Sale', 'Aromi naturali'],
    nutrition: {
      servingSize: '50g',
      energy: '140 kcal',
      fat: '12g',
      saturatedFat: '4g',
      carbs: '1g',
      sugars: '0g',
      protein: '7g',
      salt: '1.2g'
    },
    usageSuggestions: ['Ideale per antipasti', 'Perfetto per panini', 'Ottimo in insalate'],
    formats: [
      { name: 'Intero', weight: '5-10 kg' },
      { name: 'Affettato', weight: '100g' }
    ],
    image: '/images/prodotti_in_evidenza.png',
    relatedRecipes: []
  },
  en: {
    name: 'Fiorucci Product',
    tagline: 'Quality and Tradition',
    description: 'Discover the excellence of Fiorucci cured meats, produced with passion and tradition since 1850.',
    features: ['Gluten free', 'Selected meats', 'Norcinian tradition'],
    certifications: ['senza-glutine'],
    ingredients: ['Selected pork meat', 'Salt', 'Natural flavors'],
    nutrition: {
      servingSize: '50g',
      energy: '140 kcal',
      fat: '12g',
      saturatedFat: '4g',
      carbs: '1g',
      sugars: '0g',
      protein: '7g',
      salt: '1.2g'
    },
    usageSuggestions: ['Ideal for appetizers', 'Perfect for sandwiches', 'Excellent in salads'],
    formats: [
      { name: 'Whole', weight: '5-10 kg' },
      { name: 'Pre-sliced', weight: '100g' }
    ],
    image: '/images/prodotti_in_evidenza.png',
    relatedRecipes: []
  }
}

export function ProductDetailPage() {
  const { t, i18n } = useTranslation('common')
  const { category, productSlug } = useParams<{ category: string; productSlug: string }>()
  const lang = i18n.language as SupportedLanguage

  // Get product data or use default
  const product = productsData[productSlug || '']?.[lang] || defaultProduct[lang]

  return (
    <>
      <SEO
        title={`${product.name} | ${t('nav.products')}`}
        description={product.description}
        type="product"
        image={product.image}
      />

      <main id="main-content">
        <ProductHeroSection product={product} category={category || ''} />
        <ProductDetailsTabs product={product} />
        <RelatedRecipesSection recipeIds={product.relatedRecipes} />
      </main>
    </>
  )
}
